import { createSignal, createEffect, onCleanup, onMount, For, Show } from 'solid-js';
import { Portal } from 'solid-js/web';
import { createStore, reconcile } from 'solid-js/store';
import { useApi, isOk } from '../api';
import { useTranslation } from '../hooks/useTranslation';
import BotStatusBadge from './BotStatusBadge';
import Text from './Text';
import type { BotRegistryEntry } from './BotSelector';
import type { BotTaskSnapshotDto } from '../api/generated/schemas/botTaskSnapshotDto';
import styles from './FloatingBotActivityCard.module.css';

type Corner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
type TaskState = { activeTask: BotTaskSnapshotDto | null; queueLength: number };
type SafeArea = { top: number; left: number };

const CORNERS: Corner[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
const CORNER_KEY = 'vektor.floatingBotsCard.corner';
const COLLAPSED_KEY = 'vektor.floatingBotsCard.collapsed';
const SELECTED_KEY = 'vektor.floatingBotsCard.selectedBotId';
const OTHERS_EXPANDED_KEY = 'vektor.floatingBotsCard.othersExpanded';
const CORNER_OFFSET_PX = 24;
const DRAG_MARGIN_PX = 8;
const BOTS_POLL_MS = 8000;
const TASK_POLL_MS = 5000;

function readCorner(): Corner {
    const v = localStorage.getItem(CORNER_KEY);
    return (CORNERS as string[]).includes(v ?? '') ? (v as Corner) : 'top-right';
}

function readSafeArea(): SafeArea {
    const navbar = document.querySelector('.navbar');
    const navbarBottom = navbar ? Math.max(0, navbar.getBoundingClientRect().bottom) : 0;

    const sidebar = document.querySelector('.dashboard-sidebar');
    const sidebarRect = sidebar?.getBoundingClientRect();
    const sidebarRight = sidebarRect && sidebarRect.width > 0 && sidebarRect.left >= 0
        ? Math.max(0, sidebarRect.right)
        : 0;

    return { top: navbarBottom, left: sidebarRight };
}

function cornerTarget(corner: Corner, width: number, height: number, safe: SafeArea) {
    const minLeft = CORNER_OFFSET_PX + safe.left;
    const minTop = CORNER_OFFSET_PX + safe.top;
    const left = corner.endsWith('left') ? minLeft : window.innerWidth - width - CORNER_OFFSET_PX;
    const top = corner.startsWith('top') ? minTop : window.innerHeight - height - CORNER_OFFSET_PX;
    return { left, top };
}

function nearestCorner(centerX: number, centerY: number): Corner {
    const vertical = centerY < window.innerHeight / 2 ? 'top' : 'bottom';
    const horizontal = centerX < window.innerWidth / 2 ? 'left' : 'right';
    return `${vertical}-${horizontal}` as Corner;
}

function clamp(value: number, min: number, max: number) {
    return Math.min(Math.max(value, min), Math.max(min, max));
}

function isOnline(bot: BotRegistryEntry) {
    return bot.state === 'Playing';
}

export default function FloatingBotActivityCard() {
    const { t } = useTranslation();
    const api = useApi();

    const [bots, setBots] = createStore<BotRegistryEntry[]>([]);
    const [selectedId, setSelectedId] = createSignal<string>(localStorage.getItem(SELECTED_KEY) ?? '');
    const [corner, setCorner] = createSignal<Corner>(readCorner());
    const [collapsed, setCollapsed] = createSignal<boolean>(localStorage.getItem(COLLAPSED_KEY) === 'true');
    const [othersExpanded, setOthersExpanded] = createSignal<boolean>(localStorage.getItem(OTHERS_EXPANDED_KEY) !== 'false');
    const [taskStatuses, setTaskStatuses] = createStore<Record<string, TaskState>>({});
    const [pos, setPos] = createSignal({ left: CORNER_OFFSET_PX, top: CORNER_OFFSET_PX });
    const [dragging, setDragging] = createSignal(false);
    const [dragSize, setDragSize] = createSignal({ width: 0, height: 0 });
    const [dragTargetCorner, setDragTargetCorner] = createSignal<Corner | null>(null);

    let cardRef: HTMLDivElement | undefined;

    const selectedBot = () => bots.find(b => b.id === selectedId()) ?? bots[0] ?? null;
    const taskStateFor = (botId: string): TaskState => taskStatuses[botId] ?? { activeTask: null, queueLength: 0 };
    const botRank = (bot: BotRegistryEntry) => taskStateFor(bot.id).activeTask ? 0 : isOnline(bot) ? 1 : 2;
    const otherBots = () => bots
        .filter(b => b.id !== selectedBot()?.id)
        .slice()
        .sort((a, b) => botRank(a) - botRank(b));

    const select = (id: string) => {
        setSelectedId(id);
        localStorage.setItem(SELECTED_KEY, id);
    };

    const toggleCollapsed = () => {
        const next = !collapsed();
        setCollapsed(next);
        localStorage.setItem(COLLAPSED_KEY, String(next));
    };

    const toggleOthersExpanded = () => {
        const next = !othersExpanded();
        setOthersExpanded(next);
        localStorage.setItem(OTHERS_EXPANDED_KEY, String(next));
    };

    const fetchBots = async () => {
        const isInitialLoad = bots.length === 0;
        const res = await api.botRegistry.listBots();
        if (isOk(res.data)) {
            const list = res.data.value.bots as unknown as BotRegistryEntry[];
            setBots(reconcile(list, { key: 'id' }));
            if (isInitialLoad && list.length > 0 && (!selectedId() || !list.find(b => b.id === selectedId()))) {
                select(list[0].id);
            }
        }
    };

    const fetchTaskStatus = async (botId: string) => {
        const res = await api.botTask.getTaskStatus(botId);
        if (isOk(res.data)) {
            const v = res.data.value as { active: boolean; tree?: BotTaskSnapshotDto; queue: unknown[] };
            setTaskStatuses(botId, { activeTask: v.active ? (v.tree ?? null) : null, queueLength: v.queue?.length ?? 0 });
        }
    };

    const fetchAllTaskStatuses = async () => {
        if (collapsed() || bots.length === 0) return;
        await Promise.all(bots.map(b => fetchTaskStatus(b.id)));
    };

    createEffect(() => {
        collapsed();
        void fetchAllTaskStatuses();
    });

    onMount(() => {
        void fetchBots();
        const botsHandle = window.setInterval(() => {
            if (document.visibilityState === 'visible') void fetchBots();
        }, BOTS_POLL_MS);
        const taskHandle = window.setInterval(() => {
            if (document.visibilityState === 'visible') void fetchAllTaskStatuses();
        }, TASK_POLL_MS);
        onCleanup(() => {
            clearInterval(botsHandle);
            clearInterval(taskHandle);
        });
    });

    const recomputePos = () => {
        if (!cardRef || dragging()) return;
        const rect = cardRef.getBoundingClientRect();
        setPos(cornerTarget(corner(), rect.width, rect.height, readSafeArea()));
    };

    onMount(() => {
        recomputePos();
        window.addEventListener('resize', recomputePos);
        onCleanup(() => window.removeEventListener('resize', recomputePos));

        if (typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(() => recomputePos());
        if (cardRef) ro.observe(cardRef);
        const navbar = document.querySelector('.navbar');
        const sidebar = document.querySelector('.dashboard-sidebar');
        if (navbar) ro.observe(navbar);
        if (sidebar) ro.observe(sidebar);
        onCleanup(() => ro.disconnect());
    });

    createEffect(() => {
        corner();
        recomputePos();
    });

    const onHeaderPointerDown = (e: PointerEvent) => {
        if (e.button !== 0 || !cardRef) return;
        const rect = cardRef.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const offsetY = e.clientY - rect.top;
        const safe = readSafeArea();
        setDragSize({ width: rect.width, height: rect.height });
        setDragging(true);

        const onMove = (ev: PointerEvent) => {
            const left = clamp(ev.clientX - offsetX, DRAG_MARGIN_PX + safe.left, window.innerWidth - rect.width - DRAG_MARGIN_PX);
            const top = clamp(ev.clientY - offsetY, DRAG_MARGIN_PX + safe.top, window.innerHeight - rect.height - DRAG_MARGIN_PX);
            setPos({ left, top });
            setDragTargetCorner(nearestCorner(left + rect.width / 2, top + rect.height / 2));
        };
        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            setDragging(false);
            const target = dragTargetCorner();
            setDragTargetCorner(null);
            if (target) {
                setCorner(target);
                localStorage.setItem(CORNER_KEY, target);
            }
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp, { once: true });
    };

    const guidePosition = (c: Corner) => cornerTarget(c, dragSize().width, dragSize().height, readSafeArea());

    return (
        <>
            <div
                ref={cardRef}
                class={`${styles.card} ${dragging() ? styles.dragging : ''}`}
                style={{
                    position: 'fixed',
                    left: `${pos().left}px`,
                    top: `${pos().top}px`,
                    transition: dragging() ? 'none' : 'left var(--duration-normal) var(--ease-out), top var(--duration-normal) var(--ease-out)',
                }}
            >
                <div class={styles.header} onPointerDown={onHeaderPointerDown}>
                    <i class={`fa-solid fa-up-down-left-right ${styles.dragHandle}`} title={t('FLOATCARD_DRAG_HANDLE')} />
                    <i class="fa-solid fa-robot" />
                    <span class={styles.title}><Text val="FLOATCARD_TITLE" /></span>
                    <button
                        type="button"
                        class={styles.collapseBtn}
                        onClick={toggleCollapsed}
                        title={t(collapsed() ? 'FLOATCARD_EXPAND' : 'FLOATCARD_COLLAPSE')}
                    >
                        <i class={`fa-solid ${collapsed() ? 'fa-chevron-down' : 'fa-chevron-up'}`} />
                    </button>
                </div>

                <Show when={!collapsed()}>
                    <div class={styles.body}>
                        <Show when={bots.length === 0}>
                            <div class={styles.empty}>
                                <i class="fa-solid fa-robot" />
                                <span><Text val="FLOATCARD_NO_BOTS" /></span>
                            </div>
                        </Show>

                        <Show when={selectedBot()}>
                            {(bot) => (
                                <div class={styles.selectedBot}>
                                    <div class={styles.botRow}>
                                        <img class={styles.avatar} src={`https://mc-heads.net/avatar/${bot().minecraftUuid}/28`} alt="" />
                                        <span class={styles.botName}>{bot().minecraftUsername}</span>
                                        <BotStatusBadge state={bot().state} compact />
                                    </div>
                                    <div class={styles.taskInfo}>
                                        <Show when={taskStateFor(bot().id).activeTask} fallback={
                                            <span class={styles.noTask}><Text val="FLOATCARD_NO_ACTIVE_TASK" /></span>
                                        }>
                                            {(task) => (
                                                <>
                                                    <div class={styles.taskLine}>
                                                        <i class="fa-solid fa-gears" />
                                                        <span class={styles.taskName}>{task().pluginName} / {task().taskName}</span>
                                                    </div>
                                                    <div class={styles.progressBar}>
                                                        <div class={styles.progressFill} style={{ width: `${task().progress}%` }} />
                                                    </div>
                                                </>
                                            )}
                                        </Show>
                                        <Show when={taskStateFor(bot().id).queueLength > 0}>
                                            <span class={styles.queueBadge}><Text val="FLOATCARD_QUEUED" />{' '}{taskStateFor(bot().id).queueLength}</span>
                                        </Show>
                                    </div>
                                </div>
                            )}
                        </Show>

                        <Show when={otherBots().length > 0}>
                            <button type="button" class={styles.othersToggle} onClick={toggleOthersExpanded}>
                                <i class={`fa-solid ${othersExpanded() ? 'fa-chevron-down' : 'fa-chevron-right'}`} />
                                <Text val="FLOATCARD_OTHER_BOTS" /> ({otherBots().length})
                            </button>
                            <Show when={othersExpanded()}>
                                <div class={styles.otherList}>
                                    <For each={otherBots()}>
                                        {(bot) => (
                                            <button type="button" class={styles.otherRow} onClick={() => select(bot.id)}>
                                                <div class={styles.otherBotRow}>
                                                    <img class={styles.avatarSmall} src={`https://mc-heads.net/avatar/${bot.minecraftUuid}/20`} alt="" />
                                                    <span class={styles.otherName}>{bot.minecraftUsername}</span>
                                                    <BotStatusBadge state={bot.state} compact />
                                                </div>
                                                <Show when={taskStateFor(bot.id).activeTask} fallback={
                                                    <span class={styles.otherNoTask}><Text val="FLOATCARD_NO_ACTIVE_TASK" /></span>
                                                }>
                                                    {(task) => (
                                                        <>
                                                            <span class={styles.otherTaskName}>{task().pluginName} / {task().taskName}</span>
                                                            <div class={styles.otherProgressBar}>
                                                                <div class={styles.progressFill} style={{ width: `${task().progress}%` }} />
                                                            </div>
                                                        </>
                                                    )}
                                                </Show>
                                            </button>
                                        )}
                                    </For>
                                </div>
                            </Show>
                        </Show>
                    </div>
                </Show>
            </div>

            <Show when={dragging()}>
                <Portal mount={document.body}>
                    <For each={CORNERS}>
                        {(c) => {
                            const p = () => guidePosition(c);
                            return (
                                <div
                                    class={`${styles.cornerGuide} ${dragTargetCorner() === c ? styles.cornerGuideActive : ''}`}
                                    style={{
                                        left: `${p().left}px`,
                                        top: `${p().top}px`,
                                        width: `${dragSize().width}px`,
                                        height: `${dragSize().height}px`,
                                    }}
                                />
                            );
                        }}
                    </For>
                </Portal>
            </Show>
        </>
    );
}
